import React from 'react';
import I18n from 'i18n-js';
import PostalAddress from 'i18n-postal-address';
import { core_apimessages_Address } from '../schemas/rql';
import { compact } from 'lodash';

type AddressPropFields =
  | 'name'
  | 'streetAddress'
  | 'extendedAddress'
  | 'phone'
  | 'locality'
  | 'region'
  | 'country'
  | 'postalCode'
  | 'isComplete';

type Address = Pick<core_apimessages_Address, AddressPropFields>;

const LETTER_VARIANT = 'letter';
const INLINE_VARIANT = 'inline';
type Variant = 'letter' | 'inline';

interface Props {
  address: Address;
  hideCountry?: boolean;
  variant?: Variant;
}

export default function AddressSummary({
  address,
  hideCountry = false,
  variant = LETTER_VARIANT,
}: Props) {
  const countryName = address.country?.name;

  const addressLines = mapAddressToAddressLines(address);

  function showCountryName() {
    return !hideCountry && !!countryName;
  }

  function showIncompleteAddressWarning() {
    if (address.isComplete == null) { return false; }

    return !address.isComplete;
  }

  function displayVariant() {
    if (variant === INLINE_VARIANT) {
      const optionalCountryName = showCountryName() && countryName;
      return (compact([...addressLines, optionalCountryName]).join(', '));
    }

    return (
      <>
        {addressLines.map(((addressLine) => (
          <AddressLineDisplay
            data-address-summary__address-line
            key={addressLine}
            text={addressLine}
          />
        )))}
        {showCountryName() &&
          <span data-address-summary__address-line>
            {countryName}
          </span>
        }
      </>
    );
  }

  return (
    <div className="formatted-address">
      <p className="formatted-address__info">
        {displayVariant()}
      </p>
      {showIncompleteAddressWarning() &&
        <p className="formatted-address__incomplete">
          {I18n.t('discovery.addressBook.addressSummary.incompleteAddress')}
        </p>
      }
    </div>
  );
}

function AddressLineDisplay(props: { text: string }) {
  const { text } = props;

  return (
    <>
      <span>
        {text}
      </span>
      <br />
    </>
  );
}

function mapAddressToAddressLines(address: Address): string[] {
  const output = buildPostalAddress(address).output();

  if (typeof output === 'string') {
    return [output];
  }

  return output.map(line => {
    if (line.length > 1) {
      return line.join(' ');
    }

    return line[0];
  });
}

function buildPostalAddress(address: Address): PostalAddress {
  const postalAddress = new PostalAddress();
  return postalAddress
    .setFirstName(address.name)
    .setAddress1(address.streetAddress)
    .setAddress2(address.extendedAddress)
    .setCity(address.locality)
    .setState(address.region)
    .setProvince(address.region)
    .setPostalCode(address.postalCode)
    .setFormat({
      country: address.country?.countryCode,
    });
}
